import cropperjs from "cropperjs";
import {
  IConfiguration,
  IEdges,
  IImageRendererProps,
  IImageRendererState,
} from "../typings";
import { Canvas } from "canvas";

export default class Renderer {
  private props: IImageRendererProps<IConfiguration>;
  private state: Readonly<IImageRendererState>;

  public preset(
    props: IImageRendererProps<IConfiguration>,
    state: Readonly<IImageRendererState>,
  ): void {
    this.props = props;
    this.state = state;
  }

  private drawPattern(ctx: CanvasRenderingContext2D): void {
    // tslint:disable-next-line:typedef
    const img = new Image();
    img.onload = () => {
      // tslint:disable-next-line:typedef
      const pattern = ctx.createPattern(img, "repeat");
      ctx.fillStyle = pattern;
      ctx.fillRect(
        this.getImageX(),
        this.getImageY(),
        this.getImageWidth(),
        this.getImageHeight(),
      );
    };

    // img.src = this.props.conf.asset_prefix + "assets/img/pattern-overlay.png";
  }

  public renderCanvas(canvas: HTMLCanvasElement | Canvas): string {
    if (
      !this.state.image ||
      this.getImageWidth() < 1 ||
      this.getImageHeight() < 1
    ) {
      return;
    }

    const ctx: CanvasRenderingContext2D = canvas.getContext(
      "2d",
    ) as CanvasRenderingContext2D;
    const imageData: cropperjs.Data = this.props.outputController.getValue([
      "image",
      this.props.croppedDataName,
    ]);

    let offsetEdgeSkew: number = 0;
    if (this.props.renderEdgeSkew && this.state.sewingAllowance === "fold") {
      offsetEdgeSkew =
        this.props.sizeController.getEdgeSize() *
        0.7888075500316095 *
        this.getZoom() *
        this.getScale();
    }

    let offsetEdgeSkewWidth: number = 0;
    if (
      this.props.renderEdgeSkew &&
      (this.state.edges.top || this.state.edges.bottom)
    ) {
      offsetEdgeSkewWidth = offsetEdgeSkew;
    }

    let offsetEdgeSkewHeight: number = 0;
    if (
      this.props.renderEdgeSkew &&
      (this.state.edges.left || this.state.edges.right)
    ) {
      offsetEdgeSkewHeight = offsetEdgeSkew;
    }

    (this.state.canvas as HTMLCanvasElement).width = this.state.canvas
      .offsetWidth
      ? this.state.canvas.offsetWidth + offsetEdgeSkewWidth
      : this.getCanvasWidth() + offsetEdgeSkewWidth;
    (this.state.canvas as HTMLCanvasElement).height = this.state.canvas
      .offsetHeight
      ? this.state.canvas.offsetHeight + offsetEdgeSkewHeight
      : this.getCanvasHeight() + offsetEdgeSkewHeight;

    ctx.drawImage(
      this.state.image,
      imageData.x,
      imageData.y,
      imageData.width,
      imageData.height,
      this.getImageX(),
      this.getImageY(),
      this.getImageWidth(),
      this.getImageHeight(),
    );

    if (
      this.props.renderCordHole &&
      (this.props.outputController.templateController.getTemplate() as any)
        .attributes.hasCordHole?.value
    ) {
      this.drawHole(ctx);
    }

    this.drawFillEmptySpace(ctx);
    if (!this.state.edgesIncluded) {
      this.drawFillRects(ctx);
    }

    if (this.props.renderCenterFoldLines) {
      this.drawCenterfoldLines(ctx);
    }

    if (this.props.renderPattern) {
      this.drawPattern(ctx);
    }

    if (
      this.props.renderSewingAllowanceLines &&
      (this.state.sewingAllowance === "sewing-allowance" ||
        this.state.sewingAllowance === "sewing-allowance-edge-fold")
    ) {
      this.drawSewingAllowanceLines(ctx);
    }

    if (
      this.props.renderFoldLines &&
      (this.state.sewingAllowance === "fold" ||
        this.state.sewingAllowance === "sewing-allowance-edge-fold")
    ) {
      this.drawFoldEdgeLines(ctx);
    }

    return canvas.toDataURL();
  }

  private getImageWidth(): number {
    const { width }: cropperjs.Data = this.props.outputController.getValue([
      "image",
      this.props.croppedDataName,
    ]);
    return width * this.getZoom();
  }

  private getImageHeight(): number {
    const { height }: cropperjs.Data = this.props.outputController.getValue([
      "image",
      this.props.croppedDataName,
    ]);
    return height * this.getZoom();
  }

  public getCanvasWidth(): number {
    if (typeof this.props.width !== "number") {
      if (this.state.canvas) {
        if (
          typeof this.props.height === "number" &&
          this.props.height <
            this.state.canvas.offsetWidth * this.getRatioWidthToHeight()
        ) {
          return this.props.height * this.getRatioHeightToWidth();
        }
        return this.state.canvas.offsetWidth;
      }
    }

    if (
      typeof this.props.height === "number" &&
      this.props.height < this.props.width * this.getRatioWidthToHeight()
    ) {
      return this.props.height * this.getRatioHeightToWidth();
    }
    return this.props.width;
  }

  public getCanvasHeight(): number {
    if (typeof this.props.height !== "number") {
      return;
    }

    const widthToHeightRatio: number = this.getRatioWidthToHeight();
    if (this.props.height < this.props.width * widthToHeightRatio) {
      return this.props.height;
    }
    if (typeof this.props.width === "number") {
      return this.props.width * widthToHeightRatio;
    }
    if (this.state.canvas) {
      if (
        this.props.height <
        this.state.canvas.offsetWidth * widthToHeightRatio
      ) {
        return this.props.height;
      }
      return this.state.canvas.offsetWidth * widthToHeightRatio;
    }
    return;
  }

  private getImageX(): number {
    return (
      this.getImageOffsetLeft() +
      (this.state.edges.left
        ? this.state.edgesIncluded
          ? 0
          : this.props.sizeController.getEdgeSize()
        : 0) *
        this.getZoom() *
        this.getScale()
    );
  }

  private getImageY(): number {
    return (
      (this.state.edges.top
        ? this.getScale() *
          (this.state.edgesIncluded
            ? 0
            : this.props.sizeController.getEdgeSize())
        : 0) * this.getZoom()
    );
  }

  private drawFillEmptySpace(ctx: CanvasRenderingContext2D): void {
    const croppedData: Cropper.Data = this.props.outputController.getValue([
      "image",
      "cropped-data",
    ]);
    const mod: number = this.getImageHeight() / croppedData.height;
    const fillArea: { x: number; y: number } = {
      x: croppedData.x < 0 ? -croppedData.x * mod : 0,
      y: croppedData.y < 0 ? -croppedData.y * mod : 0,
    };

    if (fillArea.y !== 0) {
      ctx.fillStyle = Renderer.getImageColor(
        ctx,
        this.getImageX() + fillArea.x,
        this.getImageY() + fillArea.y + 1,
        this.getImageWidth() - fillArea.x - fillArea.x,
        1,
      );
      ctx.fillRect(
        this.getImageX() + fillArea.x,
        this.getImageY(),
        this.getImageWidth() - fillArea.x - fillArea.x,
        fillArea.y + 1,
      );
      ctx.fillStyle = Renderer.getImageColor(
        ctx,
        this.getImageX() + fillArea.x,
        this.getImageY() + this.getImageHeight() - fillArea.y - 1,
        this.getImageWidth() - fillArea.x - fillArea.x,
        1,
      );
      ctx.fillRect(
        this.getImageX() + fillArea.x,
        this.getImageY() + this.getImageHeight() - fillArea.y - 1,
        this.getImageWidth() - fillArea.x - fillArea.x,
        fillArea.y + 1,
      );
    }

    if (fillArea.x !== 0) {
      ctx.fillStyle = Renderer.getImageColor(
        ctx,
        this.getImageX() + fillArea.x + 1,
        this.getImageY() + fillArea.y,
        1,
        this.getImageHeight() - fillArea.y - fillArea.y,
      );
      ctx.fillRect(
        this.getImageX(),
        this.getImageY() + fillArea.y,
        fillArea.x + 1,
        this.getImageHeight() - fillArea.y - fillArea.y,
      );
      ctx.fillStyle = Renderer.getImageColor(
        ctx,
        this.getImageX() + this.getImageWidth() - fillArea.x - 1,
        this.getImageY() + fillArea.y,
        1,
        this.getImageHeight() - fillArea.y - fillArea.y,
      );
      ctx.fillRect(
        this.getImageX() + this.getImageWidth() - fillArea.x - 1,
        this.getImageY() + fillArea.y,
        fillArea.x + 1,
        this.getImageHeight() - fillArea.y - fillArea.y,
      );
    }

    if (fillArea.x !== 0 && fillArea.y !== 0) {
      ctx.fillStyle = Renderer.getImageColor(
        ctx,
        this.getImageX() + fillArea.x + 2,
        this.getImageY() + fillArea.y + 2,
        1,
        1,
      );
      ctx.fillRect(
        this.getImageX(),
        this.getImageY(),
        fillArea.x + 1,
        fillArea.y + 1,
      );

      ctx.fillStyle = Renderer.getImageColor(
        ctx,
        this.getImageX() + this.getImageWidth() - fillArea.x - 2,
        this.getImageY() + fillArea.y + 2,
        1,
        1,
      );
      ctx.fillRect(
        this.getImageX() + this.getImageWidth() - fillArea.x,
        this.getImageY(),
        fillArea.x + 1,
        fillArea.y + 1,
      );

      ctx.fillStyle = Renderer.getImageColor(
        ctx,
        this.getImageX() + fillArea.x + 2,
        this.getImageY() + this.getImageHeight() - fillArea.y - 2,
        1,
        1,
      );
      ctx.fillRect(
        this.getImageX(),
        this.getImageY() + this.getImageHeight() - fillArea.y - 1,
        fillArea.x + 1,
        fillArea.y + 1,
      );

      ctx.fillStyle = Renderer.getImageColor(
        ctx,
        this.getImageX() + this.getImageWidth() - fillArea.x - 2,
        this.getImageY() + this.getImageHeight() - fillArea.y - 2,
        1,
        1,
      );
      ctx.fillRect(
        this.getImageX() + this.getImageWidth() - fillArea.x - 1,
        this.getImageY() + this.getImageHeight() - fillArea.y - 1,
        fillArea.x + 1,
        fillArea.y + 1,
      );
    }
  }

  private drawFillRects(ctx: CanvasRenderingContext2D): void {
    const scale: number = this.getScale();
    const edgeSize: number =
      scale * this.props.sizeController.getEdgeSize() * this.getZoom();

    let offsetEdgeSkew: number = 0;
    if (this.props.renderEdgeSkew && this.state.sewingAllowance === "fold") {
      offsetEdgeSkew =
        this.props.sizeController.getEdgeSize() *
        0.7888075500316095 *
        this.getZoom() *
        scale;
    }

    if (this.state.edges.top) {
      ctx.fillStyle = Renderer.getImageColor(
        ctx,
        this.getImageX(),
        this.getImageY() + 1,
        this.getImageWidth(),
        1,
      );
      ctx.beginPath();
      ctx.moveTo(this.getImageX() + offsetEdgeSkew, 0);
      ctx.lineTo(this.getImageX(), edgeSize);
      ctx.lineTo(this.getImageX() + this.getImageWidth(), edgeSize);
      ctx.lineTo(this.getImageX() + this.getImageWidth() + offsetEdgeSkew, 0);
      ctx.closePath();
      ctx.fill();
    }

    if (this.state.edges.right) {
      ctx.fillStyle = Renderer.getImageColor(
        ctx,
        this.getImageX() + this.getImageWidth() - 1,
        this.getImageY(),
        1,
        this.getImageHeight(),
      );
      ctx.beginPath();
      ctx.moveTo(this.getImageX() + this.getImageWidth(), this.getImageY());
      ctx.lineTo(
        this.getImageX() + this.getImageWidth() + edgeSize,
        this.getImageY() + offsetEdgeSkew,
      );
      ctx.lineTo(
        this.getImageX() + this.getImageWidth() + edgeSize,
        this.getImageY() + this.getImageHeight() + offsetEdgeSkew,
      );
      ctx.lineTo(
        this.getImageX() + this.getImageWidth(),
        this.getImageY() + this.getImageHeight(),
      );
      ctx.closePath();
      ctx.fill();
    }

    if (this.state.edges.bottom) {
      ctx.fillStyle = Renderer.getImageColor(
        ctx,
        this.getImageX(),
        this.getImageY() + this.getImageHeight() - 1,
        this.getImageWidth(),
        1,
      );
      ctx.beginPath();
      ctx.moveTo(this.getImageX(), this.getImageY() + this.getImageHeight());
      ctx.lineTo(
        this.getImageX() + offsetEdgeSkew,
        this.getImageY() + this.getImageHeight() + edgeSize,
      );
      ctx.lineTo(
        this.getImageX() + this.getImageWidth() + offsetEdgeSkew,
        this.getImageY() + this.getImageHeight() + edgeSize,
      );
      ctx.lineTo(
        this.getImageX() + this.getImageWidth(),
        this.getImageY() + this.getImageHeight(),
      );
      ctx.closePath();
      ctx.fill();
    }

    if (this.state.edges.left) {
      ctx.fillStyle = Renderer.getImageColor(
        ctx,
        this.getImageX() + 1,
        this.getImageY(),
        1,
        this.getImageHeight(),
      );
      ctx.beginPath();
      ctx.moveTo(this.getImageOffsetLeft(), this.getImageY() + offsetEdgeSkew);
      ctx.lineTo(this.getImageOffsetLeft() + edgeSize, this.getImageY());
      ctx.lineTo(
        this.getImageOffsetLeft() + edgeSize,
        this.getImageY() + this.getImageHeight(),
      );
      ctx.lineTo(
        this.getImageOffsetLeft(),
        this.getImageY() + this.getImageHeight() + offsetEdgeSkew,
      );
      ctx.closePath();
      ctx.fill();
    }

    if (this.state.sewingAllowance !== "sewing-allowance") {
      return;
    }

    if (this.state.edges.left && this.state.edges.top) {
      ctx.fillStyle = Renderer.getImageColor(
        ctx,
        this.getImageX() + 1,
        this.getImageY(),
        1,
        1,
      );
      ctx.fillRect(
        this.getImageOffsetLeft(),
        0,
        this.state.edgesIncluded ? 0 : edgeSize + 1,
        edgeSize + 1,
      );
    }

    if (this.state.edges.top && this.state.edges.right) {
      ctx.fillStyle = Renderer.getImageColor(
        ctx,
        this.getImageX() + this.getImageWidth() - 1,
        this.getImageY(),
        1,
        1,
      );
      ctx.fillRect(
        this.getImageX() + this.getImageWidth() - 1,
        0,
        edgeSize + 1,
        edgeSize + 1,
      );
    }

    if (this.state.edges.right && this.state.edges.bottom) {
      ctx.fillStyle = Renderer.getImageColor(
        ctx,
        this.getImageX() + this.getImageWidth() - 1,
        this.getImageY() + this.getImageHeight() - 1,
        1,
        1,
      );
      ctx.fillRect(
        this.getImageX() + this.getImageWidth() - 1,
        this.getImageY() + this.getImageHeight() - 1,
        edgeSize + 1,
        edgeSize + 1,
      );
    }

    if (this.state.edges.bottom && this.state.edges.left) {
      ctx.fillStyle = Renderer.getImageColor(
        ctx,
        this.getImageX() + 1,
        this.getImageY() + this.getImageHeight() - 1,
        1,
        1,
      );
      ctx.fillRect(
        this.getImageOffsetLeft(),
        this.getImageY() + this.getImageHeight() - 1,
        edgeSize + 1,
        edgeSize + 1,
      );
    }
  }

  private drawCenterfoldLines(ctx: CanvasRenderingContext2D): void {
    const scaleZoom: number = this.getScale() * this.getZoom();

    if (this.state.fold === "horizontal") {
      const add: number =
        this.state.edgesIncluded && this.state.edges.top
          ? scaleZoom * this.props.sizeController.getEdgeSize()
          : 0;
      const min: number = this.state.edgesIncluded
        ? (this.state.edges.top
            ? scaleZoom * this.props.sizeController.getEdgeSize()
            : 0) +
          (this.state.edges.bottom
            ? scaleZoom * this.props.sizeController.getEdgeSize()
            : 0)
        : 0;
      this.drawWhiteGrayLine(
        ctx,
        this.getImageX(),
        this.getImageY() + add + (this.getImageHeight() - min) / 2,
        this.getImageWidth() +
          this.getImageX() +
          (this.state.edges.right
            ? scaleZoom *
              (this.state.edgesIncluded
                ? 0
                : this.props.sizeController.getEdgeSize())
            : 0),
        this.getImageY() + add + (this.getImageHeight() - min) / 2,
      );
    }

    if (this.state.fold === "vertical") {
      const add: number =
        this.state.edgesIncluded && this.state.edges.left
          ? scaleZoom * this.props.sizeController.getEdgeSize()
          : 0;
      const min: number = this.state.edgesIncluded
        ? (this.state.edges.left
            ? scaleZoom * this.props.sizeController.getEdgeSize()
            : 0) +
          (this.state.edges.right
            ? scaleZoom * this.props.sizeController.getEdgeSize()
            : 0)
        : 0;
      this.drawWhiteGrayLine(
        ctx,
        this.getImageX() + add + (this.getImageWidth() - min) / 2,
        this.getImageY(),
        this.getImageX() + add + (this.getImageWidth() - min) / 2,
        this.getImageHeight() +
          this.getImageY() +
          (this.state.edges.right
            ? scaleZoom * this.props.sizeController.getEdgeSize()
            : 0),
      );
    }
  }

  private drawSewingAllowanceLines(ctx: CanvasRenderingContext2D): void {
    const scale: number = this.getScale();
    const edgeSize: number =
      scale * this.props.sizeController.getEdgeSize() * this.getZoom();
    const edges: IEdges = this.state.edges;
    const edgesIncluded: boolean = this.state.edgesIncluded;

    if (edges.top) {
      this.drawWhiteGrayLine(
        ctx,
        this.getImageX() + (edgesIncluded && edges.left ? edgeSize : 0),
        edgeSize,
        this.getImageWidth() +
          this.getImageX() -
          (edgesIncluded && edges.right ? edgeSize : 0),
        edgeSize,
      );
    }

    if (this.state.edges.right) {
      const min: number = this.state.edgesIncluded ? edgeSize : 0;
      this.drawWhiteGrayLine(
        ctx,
        this.getImageX() + this.getImageWidth() - min,
        this.getImageY() + (edgesIncluded && edges.top ? edgeSize : 0),
        this.getImageX() + this.getImageWidth() - min,
        this.getImageHeight() +
          this.getImageY() -
          (edgesIncluded && edges.bottom ? edgeSize : 0),
      );
    }

    if (this.state.edges.bottom) {
      const min: number = this.state.edgesIncluded ? edgeSize : 0;
      this.drawWhiteGrayLine(
        ctx,
        this.getImageX() + (edgesIncluded && edges.left ? edgeSize : 0),
        this.getImageY() + this.getImageHeight() - min,
        this.getImageWidth() +
          this.getImageX() -
          (edgesIncluded && edges.right ? edgeSize : 0),
        this.getImageY() + this.getImageHeight() - min,
      );
    }

    if (this.state.edges.left) {
      this.drawWhiteGrayLine(
        ctx,
        this.getImageOffsetLeft() + edgeSize,
        this.getImageY() + (edgesIncluded && edges.top ? edgeSize : 0),
        this.getImageOffsetLeft() + edgeSize,
        this.getImageHeight() +
          this.getImageY() -
          (edgesIncluded && edges.bottom ? edgeSize : 0),
      );
    }
  }

  private drawFoldEdgeLines(ctx: CanvasRenderingContext2D): void {
    const scale: number = this.getScale();

    const sewingAllowanceDivider: number =
      this.props.outputController.getValue(["label", "edge"]) ===
      "sewing-allowance-edge-fold"
        ? 2
        : 1;
    const edgeSize: number =
      this.props.sizeController.getEdgeSize() / sewingAllowanceDivider;
    const scaledEdgeSize: number =
      scale * edgeSize * this.getZoom() * (sewingAllowanceDivider - 1);

    if (this.state.edges.top) {
      this.drawWhiteGrayLine(
        ctx,
        this.getImageX(),
        scale * edgeSize * this.getZoom(),
        this.getImageWidth() + this.getImageX(),
        scale * edgeSize * this.getZoom(),
      );
    }

    if (this.state.edges.right) {
      const min: number = this.state.edgesIncluded
        ? scale * this.props.sizeController.getEdgeSize() * this.getZoom()
        : 0;
      this.drawWhiteGrayLine(
        ctx,
        this.getImageX() + this.getImageWidth() - min + scaledEdgeSize,
        this.getImageY(),
        this.getImageX() + this.getImageWidth() - min + scaledEdgeSize,
        this.getImageHeight() + this.getImageY(),
      );
    }

    if (this.state.edges.bottom) {
      const min: number = this.state.edgesIncluded
        ? scale * this.props.sizeController.getEdgeSize() * this.getZoom()
        : 0;
      this.drawWhiteGrayLine(
        ctx,
        this.getImageX(),
        this.getImageY() + this.getImageHeight() - min + scaledEdgeSize,
        this.getImageWidth() + this.getImageX(),
        this.getImageY() + this.getImageHeight() - min + scaledEdgeSize,
      );
    }

    if (this.state.edges.left) {
      this.drawWhiteGrayLine(
        ctx,
        this.getImageOffsetLeft() + scale * edgeSize * this.getZoom(),
        this.getImageY(),
        this.getImageOffsetLeft() + scale * edgeSize * this.getZoom(),
        this.getImageHeight() + this.getImageY(),
      );
    }
  }

  private getZoom(): number {
    const { width, height }: cropperjs.Data =
      this.props.outputController.getValue([
        "image",
        this.props.croppedDataName,
      ]);
    if (
      typeof this.getCanvasHeight() === "number" &&
      this.getCanvasHeight() <
        this.getCanvasWidth() * this.getRatioWidthToHeight()
    ) {
      const edges: IEdges = this.state.edges;
      return (
        (this.getCanvasHeight() * this.getRatioHeightToWidth()) /
        (width +
          (edges
            ? ((edges.left
                ? this.state.edgesIncluded
                  ? 0
                  : this.props.sizeController.getEdgeSize()
                : 0) +
                (edges.right
                  ? this.state.edgesIncluded
                    ? 0
                    : this.props.sizeController.getEdgeSize()
                  : 0)) *
              this.getScale()
            : 0))
      );
    }

    const edges: IEdges = this.state.edges;
    return (
      this.getCanvasWidth() /
      (width +
        (edges
          ? ((edges.left
              ? this.state.edgesIncluded
                ? 0
                : this.props.sizeController.getEdgeSize()
              : 0) +
              (edges.right
                ? this.state.edgesIncluded
                  ? 0
                  : this.props.sizeController.getEdgeSize()
                : 0)) *
            this.getScale()
          : 0))
    );
  }

  private getRatioWidthToHeight(): number {
    const edges: IEdges = this.state.edges;
    const { width, height }: cropperjs.Data =
      this.props.outputController.getValue([
        "image",
        this.props.croppedDataName,
      ]);
    return (
      (height +
        (edges && !this.state.edgesIncluded
          ? ((edges.top ? this.props.sizeController.getEdgeSize() : 0) +
              (edges.bottom ? this.props.sizeController.getEdgeSize() : 0)) *
            this.getScale()
          : 0)) /
      (width +
        (edges && !this.state.edgesIncluded
          ? ((edges.left ? this.props.sizeController.getEdgeSize() : 0) +
              (edges.right ? this.props.sizeController.getEdgeSize() : 0)) *
            this.getScale()
          : 0))
    );
  }

  private getRatioHeightToWidth(): number {
    const edges: IEdges = this.state.edges;
    const { width, height }: cropperjs.Data =
      this.props.outputController.getValue([
        "image",
        this.props.croppedDataName,
      ]);
    return (
      (width +
        (edges
          ? ((edges.left
              ? this.state.edgesIncluded
                ? 0
                : this.props.sizeController.getEdgeSize()
              : 0) +
              (edges.right
                ? this.state.edgesIncluded
                  ? 0
                  : this.props.sizeController.getEdgeSize()
                : 0)) *
            this.getScale()
          : 0)) /
      (height +
        (edges
          ? ((edges.top
              ? this.state.edgesIncluded
                ? 0
                : this.props.sizeController.getEdgeSize()
              : 0) +
              (edges.bottom
                ? this.state.edgesIncluded
                  ? 0
                  : this.props.sizeController.getEdgeSize()
                : 0)) *
            this.getScale()
          : 0))
    );
  }

  private getImageOffsetLeft(): number {
    const difference: number =
      this.getCanvasWidth() - this.state.canvas.offsetWidth;
    if (
      this.state.canvas.offsetWidth !== 0 &&
      (difference > 1 || difference < -1)
    ) {
      return (
        (this.state.canvas.offsetWidth - this.getImageWidth()) / 2 -
        (this.state.edges.left
          ? this.getScale() *
            this.getZoom() *
            (this.state.edgesIncluded
              ? 0
              : this.props.sizeController.getEdgeSize())
          : 0)
      );
    }
    return 0;
  }

  private getScale(): number {
    const { width }: cropperjs.Data = this.props.outputController.getValue([
      "image",
      this.props.croppedDataName,
    ]);
    const edges: IEdges = this.state.edges;
    return (
      width /
      (this.props.outputController.getValue(["label", "width"]) -
        ((edges.left
          ? this.state.edgesIncluded
            ? 0
            : this.props.sizeController.getEdgeSize()
          : 0) +
          (edges.right
            ? this.state.edgesIncluded
              ? 0
              : this.props.sizeController.getEdgeSize()
            : 0)))
    );
  }

  private static getImageColor(
    ctx: CanvasRenderingContext2D,
    imageX: number,
    imageY: number,
    imageWidth: number,
    imageHeight: number,
  ): string {
    const imageData: ImageData = ctx.getImageData(
      imageX,
      imageY,
      imageWidth,
      imageHeight,
    );

    const data: Uint8ClampedArray = imageData.data;
    const totalColors: number = data.length / 4;
    const colors: any = {};

    for (let i: number = 0; totalColors > i; i++) {
      const iii: number = i * 4;
      const r: number = data[iii];
      const g: number = data[iii + 1];
      const b: number = data[iii + 2];

      let rs: string = r.toString(16);
      let gs: string = g.toString(16);
      let bs: string = b.toString(16);

      rs = rs.length === 1 ? "0" + rs : rs;
      gs = gs.length === 1 ? "0" + gs : gs;
      bs = bs.length === 1 ? "0" + bs : bs;

      const index: string = "#" + rs + gs + bs;

      if (!colors[index]) {
        colors[index] = 0;
      }
      colors[index]++;
    }
    const colorKeys: string[] = Object.keys(colors);
    return colorKeys.reduce<string>(
      (pv: string, cv: string) => (colors[cv] > colors[pv] ? cv : pv),
      colorKeys[0],
    );
  }

  private drawWhiteGrayLine(
    ctx: CanvasRenderingContext2D,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ): void {
    ctx.lineWidth = 1;

    ctx.beginPath();
    ctx.strokeStyle = "#fff";
    ctx.setLineDash([]);
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    ctx.beginPath();
    ctx.strokeStyle = "#ccc";
    ctx.setLineDash([10, 10]);
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }

  private drawHole(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = "white";
    const multiplier: number = this.getZoom() * this.getScale();
    const holeOffset: number = 5.5 * multiplier;
    const holeRadius: number = 2.5 * multiplier;
    ctx.arc(
      this.getImageX() + holeOffset,
      this.getImageY() + this.getImageHeight() / 2,
      holeRadius,
      0,
      2 * Math.PI,
    );
    ctx.fill();
  }
}
